<template>
  <TheTemplateM class="p-the-page-m08" :step="step" :step-options="stepOptions">
    <template #heading>メールアドレス変更</template>

    <template #body>
      <div class="p-the-page-m08__form c-form-inner">
        <form ref="form" @submit.prevent="submit">
          <div class="p-form-group">
            <FormLabel for="name" :required="true">メールアドレス</FormLabel>
            <FormUneditableText>{{ currentEmail }}</FormUneditableText>
          </div>

          <div class="p-form-group">
            <FormLabel for="newEmail" :required="true"
              >新しいメールアドレス</FormLabel
            >
            <InputText
              id="newEmail"
              v-model="newEmail"
              type="text"
              name="newEmail"
              :error="errors.newEmail"
              placeholder="メールアドレス"
            />
            <ValidationErrorText v-show="currentAndNewEmailIsSame">
              {{ currentAndNewEmailIsSameError }}
            </ValidationErrorText>
            <ValidationErrorText v-show="errors.newEmail">
              {{ errors.newEmail }}
            </ValidationErrorText>
          </div>

          <div class="p-form-group">
            <FormLabel for="newEmailConfirm" :required="true"
              >新しいメールアドレス（確認用）</FormLabel
            >
            <InputText
              id="newEmailConfirm"
              v-model="newEmailConfirm"
              type="text"
              name="newEmailConfirm"
              :error="errors.newEmailConfirm"
              placeholder="メールアドレス再入力をお願いします。"
            />
            <ValidationErrorText v-show="errors.newEmailConfirm">
              {{ errors.newEmailConfirm }}
            </ValidationErrorText>
          </div>

          <div class="p-form-group">
            <FormLabel for="code1" :required="true"
              >認証コード（4桁）</FormLabel
            >
            <p class="p-the-page-m08__attention">
              メールアドレスの変更には4桁の数字での本人認証が必要となります。以下にお好きな数字
              4桁を設定してください。
              <br />※連続した数字4桁（1234等）や、同数字（1111等）はセキュリティ上設定いただけません。
            </p>
            <div class="p-the-page-m08__code">
              <FormCode :code="code" @set="setCode" />
              <input
                v-model="code"
                type="hidden"
                name="code"
                :class="{ 'is-error': errors.code }"
              />
              <ValidationErrorText v-show="errors.code">
                {{ errors.code }}
              </ValidationErrorText>
            </div>
          </div>

          <div class="p-form-button-group">
            <div class="p-form-button-group__vertical">
              <ButtonMain
                type="submit"
                :disabled="isInvalid || currentAndNewEmailIsSame"
              >
                変更
              </ButtonMain>
            </div>
            <div class="p-form-button-group__vertical">
              <ButtonMain :tag="'a'" href="/my-page/" :kind="'cancel'">
                キャンセル
              </ButtonMain>
            </div>
          </div>
        </form>
      </div>
    </template>
  </TheTemplateM>
</template>

<script lang="ts">
import { Vue, Options } from "vue-class-component";
import { Watch } from "vue-property-decorator";
import * as yup from "yup";

import TheTemplateM from "@/general/components/templates/TheTemplateM.vue";
import InputText from "@/common/components/atoms/InputText.vue";
import ButtonMain from "@/common/components/atoms/ButtonMain.vue";
import FormCode from "@/common/components/organisms/FormCode.vue";
import FormLabel from "@/common/components/organisms/FormLabel.vue";
import FormUneditableText from "@/common/components/atoms/FormUneditableText.vue";
import ValidationErrorText from "@/common/components/atoms/ValidationErrorText.vue";

import LocalStorage from "@/general/utils/storage";
import AuthStorage from "@/general/utils/auth";
import { myPageEmailReset } from "@/general/store/api";
import commons from "@/common/store/modules/commons";

@Options({
  components: {
    TheTemplateM,
    InputText,
    ButtonMain,
    FormCode,
    FormLabel,
    FormUneditableText,
    ValidationErrorText
  },
  watch: {
    newEmail() {
      this.currentAndNewEmailIsSame = this.currentEmail === this.newEmail;
    }
  }
})
export default class MyPageEmailSettings extends Vue {
  step = 1;
  stepOptions = [
    { step: 1, label: "入力" },
    { step: 2, label: "メール確認" },
    { step: 3, label: "本人認証" },
    { step: 4, label: "完了" }
  ];

  newEmail = "";
  newEmailConfirm = "";
  code = "";
  currentAndNewEmailIsSame = false;
  currentAndNewEmailIsSameError =
    "既存のメールアドレスと新しいメールアドレスを同じにすることはできません";

  userInfo: any = {};
  errors: Record<string, string> = {};
  isInvalid = false;

  breadcrumb = [
    { text: "", url: "/home", pageType: "home" },
    { text: "マイページ", url: "/my-page/", pageType: "lower" },
    { text: "メールアドレス変更", url: "", pageType: "lower" }
  ];

  get currentEmail() {
    return this.userInfo?.email;
  }

  mounted() {
    AuthStorage.checkLogin();
  }

  async created() {
    commons.getBreadcrumb(this.breadcrumb);
    this.userInfo = JSON.parse(LocalStorage.getItem("userDetails") as string);

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    if (urlParams.get("back_from") === "email_confirmation") {
      this.newEmail = LocalStorage.getItem("new_email") || "";
      this.newEmailConfirm = LocalStorage.getItem("new_email") || "";
    }
  }

  async submit() {
    this.errors = {};
    this.isInvalid = false;

    const schema = yup.object({
      newEmail: yup
        .string()
        .required("新しいメールアドレスを入力してください。")
        .email("新しいメールアドレスの形式が正しくありません。")
        .max(256, "新しいメールアドレスは256文字以内を入力してください。"),
      newEmailConfirm: yup
        .string()
        .required("新しいメールアドレス（確認用）を入力してください。")
        .oneOf(
          [this.newEmail],
          "新しいメールアドレス（確認用）が一致しません。"
        ),
      code: yup
        .string()
        .required("認証コード（4桁）を入力してください。")
        .matches(/^\d{4}$/, "認証コード（4桁）は数字4桁で入力してください。")
        .test(
          "not-same",
          "同数字（1111等）はセキュリティ上設定いただけません。",
          (val) => {
            return val !== val[0].repeat(4);
          }
        )
        .test(
          "not-serial",
          "連続した数字4桁（1234等）はセキュリティ上設定いただけません。",
          (val) => {
            const asc = "0123456789";
            const desc = "9876543210";
            return !asc.includes(val) && !desc.includes(val);
          }
        )
    });

    try {
      await schema.validate(
        {
          newEmail: this.newEmail,
          newEmailConfirm: this.newEmailConfirm,
          code: this.code
        },
        { abortEarly: false }
      );
    } catch (err: any) {
      err.inner.forEach((e: any) => {
        this.errors[e.path] = e.message;
      });
      this.isInvalid = true;
      return;
    }

    const OrgSpecificData = LocalStorage.getItem("OrgSpecificData") as string;
    const org = JSON.parse(OrgSpecificData);
    const page_title = org.page_title;

    const emailReset = await myPageEmailReset({
      email: this.newEmail,
      auth_code: this.code,
      page_title
    });

    LocalStorage.setItem("new_email", this.newEmail);
    LocalStorage.setItem("code", this.code);

    if (emailReset && emailReset.id) {
      location.href = "/my-page/email-confirmation/";
    }
  }

  setCode(code: string) {
    this.code = code;
  }
}
</script>

<style lang="scss" scoped>
.p-the-page-m08 {
  &__form {
    margin-top: rem(68);

    @include sp {
      margin-top: rem(28);
    }
  }

  &__attention {
    color: $error;
    font-size: rem(14);
    line-height: (24/14);
  }

  &__code {
    margin-top: rem(12);
  }
}
</style>
